import { cloneDeep } from 'lodash-es';
import { BetDateType, Currency } from '@/constants';
import { todayDateRangeInUserTimeZone } from '@/helpers';
import { applyCustomerPlDatePeriodPreset } from '@/helpers/applyCustomerPlDatePeriodPreset';
import { createOptionalSportFilter, resetOptionalSportFilter } from '@/helpers/sportFilter';
import { CustomerPlItem } from '@/models/customerPl';
import { createCustomerPlTableHeaders } from '@/pages/accounting/customerPl/CustomerPlTableHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { customerPlService } from '@/services/customerPlService';
import { createKeyedLoadingGenerationTracker, createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { useUserStore } from '@/stores/user';
import { CustomerPlSection, ICustomerPlFilters, ICustomerPlRowAPI } from '@/types/customerPl';
import { IPageOptions } from '@/types/page';
import type { DataTableHeader } from '@/types/vuetify';

const CUSTOMER_PL_SECTIONS: CustomerPlSection[] = ['total', 'sport', 'periodType', 'marketType', 'market', 'bookmaker', 'leagueLimits', 'customLeague'];

function mapRows(rows: ICustomerPlRowAPI[] | undefined): CustomerPlItem[] {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => new CustomerPlItem(row));
}

function createCustomerPlStoreSetup(storeId: string) {
    // --- state ---
    const itemsBySection = reactive<Record<CustomerPlSection, CustomerPlItem[]>>({
        total: [],
        sport: [],
        periodType: [],
        marketType: [],
        market: [],
        bookmaker: [],
        leagueLimits: [],
        customLeague: [],
    });

    const loading = ref(false);
    const aggregateLoadGeneration = createLoadingGenerationTracker();
    const sectionLoadGeneration = createKeyedLoadingGenerationTracker();
    const loadingBySection = reactive<Record<CustomerPlSection, boolean>>({
        total: false,
        sport: false,
        periodType: false,
        marketType: false,
        market: false,
        bookmaker: false,
        leagueLimits: false,
        customLeague: false,
    });

    // --- filters ---
    const filters = reactive<ICustomerPlFilters>({
        betDateType: {
            value: BetDateType.PlacementLondon,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        betUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        sportId: createOptionalSportFilter(),
        betDatePeriod: {
            value: 'lifetime',
            title: 'customerPl.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bets.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betTradingTypeId: {
            value: null,
            title: 'bets.tradingType',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        displayedCurrencyId: {
            value: Currency.Euro,
            title: 'common.displayedCurrency',
            allowSaveState: true,
            disableClear: true,
        },
        betBetVsOpen: {
            value: null,
            title: 'bets.betVsOpen',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const userStore = useUserStore();
    const filterPanel = useFilterPanelState(filters);
    const availableFilters = computed(() => filterPanel.availableFilters.value.filter((filter) => filter.key !== 'betBetVsOpen' || userStore.isAdmin()));
    const visibleFilters = computed(() => filterPanel.visibleFilters.value.filter((filterName) => filterName !== 'betBetVsOpen' || userStore.isAdmin()));
    const { hiddenFilters, visibleFiltersMobile, filterOrder, useButtonForFiltersApply, saveFiltersState } = filterPanel;

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 500,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        filters.betDateType.value = BetDateType.PlacementLondon;
        filters.betUserId.value = null;
        resetOptionalSportFilter(filters.sportId);
        filters.betDatePeriod.value = 'lifetime';
        filters.betDate.value = todayDateRangeInUserTimeZone();
        filters.betTradingTypeId.value = null;
        filters.betEventPeriodId.value = null;
        filters.betEventType.value = null;
        filters.displayedCurrencyId.value = Currency.Euro;
        filters.betBetVsOpen.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createCustomerPlTableHeaders());

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'customerPl',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    function clearSectionItems() {
        for (const section of CUSTOMER_PL_SECTIONS) {
            itemsBySection[section] = [];
        }
    }

    function invalidateInFlightLoads() {
        aggregateLoadGeneration.next();
        for (const section of CUSTOMER_PL_SECTIONS) {
            sectionLoadGeneration.next(section);
            loadingBySection[section] = false;
        }
        loading.value = false;
    }

    function buildListParams() {
        const filtersCopy = cloneDeep(filters);
        applyCustomerPlDatePeriodPreset(filtersCopy);
        // UI-only preset — API gets betDateFrom/To (or none for lifetime)
        filtersCopy.betDatePeriod.value = null;

        return {
            filters: filtersCopy,
            options: {
                sortBy: options.value.sortBy,
                rowsPerPageItems: options.value.rowsPerPageItems,
                page: options.value.page,
                itemsPerPage: options.value.itemsPerPage,
            },
            requiredData: [] as string[],
            requiredDictionaries: [] as string[],
        };
    }

    async function loadSection(section: CustomerPlSection) {
        const sectionLoadId = sectionLoadGeneration.next(section);
        loadingBySection[section] = true;
        try {
            const response = await customerPlService.list(section, {
                ...buildListParams(),
                requestKey: `customer-pl:${section}`,
            });
            if (!sectionLoadGeneration.isLatest(section, sectionLoadId)) return;
            itemsBySection[section] = mapRows(response.data.rows);
        } catch (e) {
            if (isRequestCanceled(e)) return;
            if (!sectionLoadGeneration.isLatest(section, sectionLoadId)) return;
            itemsBySection[section] = [];
            throw e;
        } finally {
            if (sectionLoadGeneration.isLatest(section, sectionLoadId)) {
                loadingBySection[section] = false;
            }
        }
    }

    async function loadAllData() {
        if (!filters.betUserId.value?.id) {
            invalidateInFlightLoads();
            clearSectionItems();
            return;
        }

        const aggregateLoadId = aggregateLoadGeneration.next();
        loading.value = true;
        try {
            const sectionsToLoad = CUSTOMER_PL_SECTIONS.filter(
                (section) => (section !== 'sport' || filters.sportId.value == null) && (section !== 'marketType' || filters.betEventType.value == null)
            );

            if (filters.sportId.value != null) {
                sectionLoadGeneration.next('sport');
                itemsBySection.sport = [];
                loadingBySection.sport = false;
            }

            if (filters.betEventType.value != null) {
                sectionLoadGeneration.next('marketType');
                itemsBySection.marketType = [];
                loadingBySection.marketType = false;
            }

            await Promise.all(sectionsToLoad.map((section) => loadSection(section)));
        } finally {
            if (aggregateLoadGeneration.isLatest(aggregateLoadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadAllData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadAllData();
        }
    }

    function clearData() {
        invalidateInFlightLoads();
        clearSectionItems();
        options.value.page = 1;
    }

    // --- return ---
    return {
        storeId,
        itemsBySection,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        loadingBySection,
        options,
        loadAllData,
        updateOptions,
        clearData,
        stats: undefined,
        hasExport: false,
        hasRefresh: true,
    };
}

export function useCustomerPlStore(storeId: string) {
    const useStore = defineStore(storeId, () => createCustomerPlStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'customerPl'),
    });
    return useStore();
}

export type CustomerPlStoreSetup = ReturnType<typeof useCustomerPlStore>;
